import React, { useMemo } from 'react'

import { useGetTagsQuery } from '@/src/api/rtkQuery/tagsApi'
import { domainCountry } from '@/src/constants/domain'
import { useIsHeya } from '@/src/hooks/useThemeKey'
import DesktopCouponSectionShimmer from '@/src/components/desktop/srp/loadingShimmers/DesktopCouponSectionShimmer'
import { JsonsData } from '@/src/types/flight'
import { useLocale } from 'next-intl'

import { CouponSection } from './couponSection'
import { MOBILE } from '@/src/constants/deviceTypes'

/** Normalizes targetLink to path from "/offers" till end (strips query/hash). Works for any URL or path. */
function normalizeOfferTargetLink(targetLink: string | undefined): string {
    if (!targetLink || typeof targetLink !== 'string') return ''
    const trimmed = targetLink.trim()
    const idx = trimmed.indexOf('/offers')
    if (idx === -1) return ''
    return trimmed.slice(idx).split('?')[0].split('#')[0]
}

interface CouponSectionWrapperProps {
    jsons: JsonsData | undefined
    isRT: boolean | undefined
    deviceType?: 'DESKTOP' | 'MOBILE'
}

const CouponSectionWrapper: React.FC<CouponSectionWrapperProps> = ({ jsons, isRT = false, deviceType }) => {
    const locale = useLocale()
    const isHeya = useIsHeya()

    // If jsons data is available and it's NOT international, don't show coupon section
    if (jsons && jsons.searchType?.isIntl === false) {
        return null
    }

    // Default to international (true) for initial load
    const isIntl = jsons?.searchType?.isIntl ?? true

    // Build query params for API - much cleaner!
    const queryParams = useMemo(
        () => ({
            domain: domainCountry,
            ...(isRT ? { isRT: true } : { isOW: true }),
            ...(isIntl ? { intl: true } : { dom: true }),
            ...(isHeya ? { heyaApplicable: true } : {})
        }),
        [isRT, isIntl, isHeya]
    )

    // Fetch tags data - no skip condition, always fetch for international
    const { data: tags = [], isLoading } = useGetTagsQuery(queryParams)

    // Transform tags data for presentation with locale fallback; normalize targetLink to path-only /offers/{slug}
    const coupons = useMemo(() => {
        return tags.map((tag, index) => {
            const langData = locale === 'ar' ? tag.ar : tag.en
            const fallbackData = tag.en
            const normalizedPath = normalizeOfferTargetLink(tag.targetLink || '')
            let targetLink = normalizedPath ? `/${locale}${normalizedPath}` : ''
            if (deviceType === MOBILE && targetLink) {
                targetLink += "?hideBookNow=true"
            }
            return {
                id: tag._id || `coupon-${index}`,
                heading: langData?.heading || fallbackData?.heading || '',
                description: langData?.description || fallbackData?.description || '',
                targetLink
            }
        })
    }, [tags, locale])

    // Show shimmer while loading
    if (isLoading) {
        return <DesktopCouponSectionShimmer />
    }

    // Don't render if no coupons
    if (coupons.length === 0) {
        return null
    }

    return <CouponSection coupons={coupons} isMobile={deviceType === MOBILE} />
}

export { CouponSectionWrapper }
export default CouponSectionWrapper
